import React, { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Phone, Search, X, Loader2, ShieldAlert, MapPin, User, FileText, Siren, Info } from "lucide-react";

const LABEL_COLORS = {
  "CURRENTLY VERIFIED": "#10B981",
  "PREVIOUSLY ASSOCIATED": "#F59E0B",
  "CALLER-PROVIDED": "#3B82F6",
  "SUBSCRIBER RETURN": "#6B7280",
  "UNVERIFIED": "#F97316",
  "POSSIBLY REASSIGNED": "#EF4444",
  "NO CAD HISTORY": "#6B7280",
};

const Tag = ({ label }) => (
  <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 border"
    style={{ color: LABEL_COLORS[label] || "#9CA3AF", borderColor: (LABEL_COLORS[label] || "#9CA3AF") + "80" }}>
    {label}
  </span>
);

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || "—"; } };

// ALI subscriber directory status + REBUILD (facilities, premises, PSAP, prior CAD callers) + CSV import (phone,name[,kind][,address]).
function AliDirectoryBar() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);
  const load = () => client.get("/ali/stats").then((r) => setStats(r.data)).catch(() => {});
  React.useEffect(() => { load(); }, []);
  const rebuild = async () => { setBusy(true); try { const { data } = await client.post("/ali/rebuild"); toast.success(`ALI directory loaded — ${data.total} subscribers`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Rebuild failed"); } finally { setBusy(false); } };
  const imp = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await client.post("/ali/import", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.imported} subscribers (total ${data.total})`); load(); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ali-directory-bar" className="flex items-center gap-2 flex-wrap border border-[#2C2D35] bg-[#0d0e11] px-2 py-1.5">
      <span className="font-mono text-[10px] text-[#9CA3AF]">ALI SUBSCRIBER DIRECTORY · <b className="text-white" data-testid="ali-total">{stats ? stats.total : "…"}</b> numbers loaded{stats?.by_source ? ` (${Object.entries(stats.by_source).map(([k, v]) => `${k.replace("import:", "csv ")} ${v}`).join(" · ")})` : ""}</span>
      <span className="ml-auto flex gap-1.5">
        <button data-testid="ali-rebuild" onClick={rebuild} disabled={busy} className="px-2 py-1 text-[10px] font-head border border-[#3B82F6]/60 text-[#93C5FD] hover:bg-[#3B82F6]/20 disabled:opacity-50">{busy ? "WORKING…" : "LOAD / REFRESH NUMBERS"}</button>
        <button data-testid="ali-import" onClick={() => fileRef.current?.click()} disabled={busy} className="px-2 py-1 text-[10px] font-head border border-[#2C2D35] text-[#9CA3AF] hover:text-white disabled:opacity-50">IMPORT CSV</button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={imp} />
      </span>
    </div>
  );
}


export default function TelephoneIntel({ open, onClose, defaultNumber = "" }) {
  const [number, setNumber] = useState(defaultNumber);
  const [reason, setReason] = useState("Active CAD Call");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  React.useEffect(() => {
    if (open) { setNumber(defaultNumber || ""); setRes(null); setBusy(false); }
  }, [open, defaultNumber]);
  if (!open) return null;

  const run = async () => {
    if (!number.trim()) { toast.error("Enter a telephone number"); return; }
    if (!reason.trim()) { toast.error("A reason is required — every search is audited (CJIS/privacy)."); return; }
    setBusy(true); setRes(null);
    try {
      const { data } = await client.get("/telephone/intelligence", { params: { number, reason } });
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    finally { setBusy(false); }
  };

  const inp = "flex-1 bg-[#0d0e11] border border-[#2C2D35] px-3 py-2 text-sm outline-none focus:border-[#3B82F6] font-mono text-white";

  return (
    <div data-testid="tel-intel-modal" className="fixed inset-0 z-[999] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="w-full max-w-3xl bg-[#141518] border border-[#2C2D35] my-6">
        {/* header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#2C2D35] bg-[#0d0e11]">
          <Phone size={16} className="text-[#3B82F6]" />
          <div className="font-head font-semibold text-sm text-white">ANI/ALI Telephone Intelligence · Subscriber Inquiry · CAD Call History</div>
          <button data-testid="tel-intel-close" onClick={onClose} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* search */}
          <div className="flex gap-2">
            <div className="flex items-center gap-2 flex-1 bg-[#0d0e11] border border-[#2C2D35] px-2 focus-within:border-[#3B82F6]">
              <Search size={14} className="text-[#9CA3AF]" />
              <input data-testid="tel-intel-input" value={number} onChange={(e) => setNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()} placeholder="(410) 555-0123"
                className="flex-1 bg-transparent py-2 text-sm outline-none font-mono text-white" autoFocus />
            </div>
            <button data-testid="tel-intel-search" onClick={run} disabled={busy}
              className="flex items-center gap-1.5 px-4 bg-[#3B82F6] hover:bg-[#2563eb] text-white text-sm font-head disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
            </button>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="tel-intel-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              className="flex-1 bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none focus:border-[#3B82F6] font-mono text-white">
              {["Active CAD Call", "Call-back / Follow-up", "Repeat Caller Review", "Threat Assessment", "Supervisor Directed", "Records Correction"].map((r) => <option key={r}>{r}</option>)}
            </select>
            <span className="font-mono text-[9px] text-[#F59E0B]">EVERY SEARCH IS LOGGED — USER · REASON · DATE/TIME</span>
          </div>
          <AliDirectoryBar />

          {!res && !busy && (
            <div className="text-xs font-mono text-[#9CA3AF] border border-[#2C2D35] p-3">
              Searches the current active 9-1-1 call, prior CAD calls, associated addresses, caller-provided names,
              related incidents, verified premise/responder-safety alerts, and case numbers linked to this number.
            </div>
          )}

          {res && (
            <div data-testid="tel-intel-return" className="border border-[#2C2D35]">
              {/* return header */}
              <div className="px-3 py-2 bg-[#0d0e11] border-b border-[#2C2D35] flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] text-[#9CA3AF]">TELEPHONE HISTORY RETURN</span>
                <span className="font-head font-bold text-white">{res.number}</span>
                <Tag label={res.verification?.primary_label} />
                {res.reassignment_warning && <Tag label="POSSIBLY REASSIGNED" />}
              </div>

              <div className="p-3 space-y-3">
                {/* summary grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Stat label="Prior CAD contacts" value={res.prior_cad_contacts} />
                  <Stat label="Simultaneous active" value={res.simultaneous_active_calls} />
                  <Stat label="Assoc. addresses" value={res.associated_addresses?.length || 0} />
                  <Stat label="Most recent" value={res.most_recent_contact ? fmtDate(res.most_recent_contact).split(",")[0] : "—"} small />
                </div>

                {/* current active */}
                {res.current_active_call && (
                  <Block icon={Siren} color="#EF4444" title="CURRENT ACTIVE CALL">
                    <div className="text-xs font-mono text-white">
                      {res.current_active_call.cc} · {res.current_active_call.call_type} @ {res.current_active_call.address}
                    </div>
                  </Block>
                )}

                {/* caller names */}
                <Block icon={User} color="#3B82F6" title="CALLER-PROVIDED NAMES">
                  {res.caller_names_provided?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {res.caller_names_provided.map((n) => (
                        <span key={n} className="text-xs font-mono text-white flex items-center gap-1">{n} <Tag label="CALLER-PROVIDED" /></span>
                      ))}
                    </div>
                  ) : <Empty>No caller name recorded.</Empty>}
                </Block>

                {/* addresses */}
                <Block icon={MapPin} color="#F59E0B" title="ASSOCIATED ADDRESSES">
                  {res.associated_addresses?.length ? (
                    <ul className="space-y-1">
                      {res.associated_addresses.map((a) => (
                        <li key={a} className="text-xs font-mono text-white flex items-center gap-1.5"><span className="flex-1">{a}</span><Tag label="PREVIOUSLY ASSOCIATED" /></li>
                      ))}
                    </ul>
                  ) : <Empty>No associated addresses.</Empty>}
                </Block>

                {/* safety alerts */}
                {res.safety_alerts?.length > 0 && (
                  <Block icon={ShieldAlert} color="#DC2626" title="PREMISE / RESPONDER-SAFETY ALERTS">
                    {res.safety_alerts.map((s, i) => (
                      <div key={i} className="text-xs font-mono text-[#FCA5A5]">⚠ {s.address}: {s.summary}</div>
                    ))}
                    <div className="text-[10px] font-mono text-[#6B7280] mt-1">Reliability varies (REPORTED / CORROBORATED / CONFIRMED) — verify against the premise record before relying.</div>
                  </Block>
                )}

                {/* incident history */}
                <Block icon={FileText} color="#8B5CF6" title={`PRIOR INCIDENTS (${res.incidents?.length || 0})`}>
                  {res.incidents?.length ? (
                    <div className="space-y-1 max-h-56 overflow-y-auto">
                      {res.incidents.map((i) => (
                        <div key={i.cc} data-testid={`tel-intel-incident-${i.cc}`} className="border border-[#2C2D35] bg-[#0d0e11] px-2 py-1.5">
                          <div className="flex items-center gap-2 text-[11px] font-mono">
                            <span className="text-[#3B82F6]">{i.cc}</span>
                            <span className="uppercase text-[#9CA3AF]">{i.discipline}</span>
                            <span className="text-white flex-1 truncate">{i.call_type}</span>
                            <span className="text-[#9CA3AF]">{fmtDate(i.date).split(",")[0]}</span>
                          </div>
                          <div className="text-[10px] font-mono text-[#9CA3AF] mt-0.5">
                            {i.address}{i.apt ? ` #${i.apt}` : ""} · role: {i.role} · {i.status}{i.units?.length ? ` · units: ${i.units.join(", ")}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <Empty>No prior CAD incidents for this number.</Empty>}
                </Block>

                {/* case numbers */}
                {res.case_numbers?.length > 0 && (
                  <Block icon={FileText} color="#10B981" title="ASSOCIATED CASE NUMBERS">
                    <div className="flex flex-wrap gap-1.5">
                      {res.case_numbers.map((c) => <span key={c} className="font-mono text-xs text-white">{c}</span>)}
                    </div>
                  </Block>
                )}

                {/* subscriber inquiry */}
                <Block icon={Info} color="#6B7280" title="SUBSCRIBER / CARRIER INQUIRY">
                  <div className="flex items-center gap-2 mb-1"><Tag label="SUBSCRIBER RETURN" /><span className="font-mono text-[11px] text-[#9CA3AF]">{res.subscriber?.status}</span></div>
                  <div className="text-[11px] font-mono text-[#9CA3AF]">Carrier: <span className="text-[#F97316]">UNVERIFIED</span> · Line type: <span className="text-[#F97316]">UNVERIFIED</span> · Text-to-911: <span className="text-[#F97316]">UNVERIFIED</span></div>
                  <div className="text-[10px] font-mono text-[#6B7280] mt-1">{res.subscriber?.note}</div>
                </Block>

                {/* alternate formats */}
                <div className="text-[10px] font-mono text-[#6B7280]">Alternate formats: {res.alternate_formats?.join("  ·  ")}</div>

                {/* disclaimer */}
                <div data-testid="tel-intel-disclaimer" className="border border-[#DC2626]/50 bg-[#1a0b0c] px-3 py-2 flex gap-2">
                  <ShieldAlert size={14} className="text-[#DC2626] shrink-0 mt-0.5" />
                  <div className="text-[11px] font-mono text-[#FCA5A5] leading-snug"><b>WARNING —</b> {res.disclaimer}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, small }) => (
  <div className="border border-[#2C2D35] bg-[#0d0e11] px-2 py-1.5">
    <div className="font-mono text-[9px] text-[#9CA3AF] uppercase">{label}</div>
    <div className={`font-head font-bold text-white ${small ? "text-xs mt-0.5" : "text-lg"}`}>{value}</div>
  </div>
);

const Block = ({ icon: Icon, color, title, children }) => (
  <div className="border border-[#2C2D35]">
    <div className="px-2 py-1.5 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-[11px] font-semibold flex items-center gap-1.5" style={{ color }}>
      <Icon size={12} /> {title}
    </div>
    <div className="p-2">{children}</div>
  </div>
);

const Empty = ({ children }) => <div className="text-[11px] font-mono text-[#6B7280]">{children}</div>;
